import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import Form from 'mobx-react-form';
import { camelCase, find, get, isEqual } from 'lodash';
import { Typography, Button, Box, IconButton, Menu, MenuItem } from '@mui/material';
import { GridColDef, GridRowParams } from '@mui/x-data-grid';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import DeleteIcon from '@mui/icons-material/Delete';

import { divide } from 'shared/utils/numberOperations';
import { AddExpenseDialog } from '../AddExpenseDialog';
import { expenseFormHelpers } from '../operatingExpenseFormConfig';
import { Table } from 'components/shared/Table';
import { getBaseColumns } from './helpers';
import { EXPENSE_HISTORY_TABLE_BASE_SX } from './constants';

interface ExpenseHistoryTableProps {
  form: Form;
}

interface ExpenseRow {
  id: string;
  category: string;
  isTotal?: boolean;
  average: number;
  [key: string]: any;
}

const ExpenseHistoryTable = observer(({ form }: ExpenseHistoryTableProps) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRowId, setSelectedRowId] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [totalValues, setTotalValues] = useState({});
  const [totalExcludingREValues, setTotalExcludingREValues] = useState({});
  const expenseHistoryField = form.$('incomeApproach.expenses.expenseHistory');
  const expenseCategoriesField = form.$('incomeApproach.expenses.expenseCategories');
  const expenseNameField = form.$('expenseDialog.expenseName');

  const handleClickOpen = () => setIsModalOpen(true);
  const handleClickClose = () => {
    setIsModalOpen(false);
    expenseNameField.set(null);
  };

  const expenseHistory = expenseHistoryField.value || [];
  const expenseCategories = expenseCategoriesField.value;

  useEffect(() => {
    calculateTotalValues();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expenseHistory, expenseCategories]);

  const deleteColumn = useCallback(
    (expenseHistoryId: string) => {
      expenseFormHelpers.removeExpenseYear(form, expenseHistoryId);
    },
    [form]
  );

  const handleProcessRowUpdate = useCallback(
    (newRow: any, oldRow: any) => {
      if (newRow.isTotal || isEqual(newRow, oldRow)) {
        return oldRow;
      }
      const { category } = newRow;
      const yearValues: number[] = [];
      expenseHistory.forEach((expenseHistoryItem: any) => {
        const fieldName = `${expenseHistoryItem.period}-${expenseHistoryItem.year}`;
        const expenses = expenseHistoryItem.expenses || [];

        if (newRow[fieldName] !== oldRow[fieldName]) {
          const newValue = newRow[fieldName];
          expenseFormHelpers.updateExpenseValue(form, category, fieldName, parseFloat(newValue) || 0);
          yearValues.push(newValue);
        } else {
          const expenseValue = find(expenses, (expense) => expense.category === category)?.value || 0;
          yearValues.push(expenseValue);
        }
      });
      const rowTotal = yearValues.reduce((sum, val) => sum + val, 0);
      newRow.average = divide(rowTotal, expenseHistory.length);

      return newRow;
    },
    [form, expenseHistory]
  );

  const handleMenuClick = (event: any, rowId: any) => {
    setAnchorEl(event.currentTarget);
    setSelectedRowId(rowId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedRowId(null);
  };

  const handleDeleteCategory = useCallback(() => {
    if (!selectedRowId) return;

    const categoryToDelete = expenseCategories.find((expenseCategory: any) => expenseCategory.key === selectedRowId);
    if (!categoryToDelete) {
      handleMenuClose();
      return;
    }

    expenseFormHelpers.removeExpenseCategory(form, categoryToDelete.category);
    handleMenuClose();
  }, [expenseCategories, form, selectedRowId]);

  const addCategory = (modalFormValues: { expenseName: string }) => {
    const { expenseName } = modalFormValues;
    const newKey = camelCase(expenseName);
    expenseFormHelpers.addExpenseCategory(form, expenseName, newKey);
    handleClickClose();
  };

  const calculateTotalValues = () => {
    const totalValues: any = {};
    const totalExcludingREValues: any = {};

    expenseHistory.forEach((expenseHistoryItem: any) => {
      const fieldName = `${expenseHistoryItem.period}-${expenseHistoryItem.year}`;
      const { expenses } = expenseHistoryItem;
      const total = expenses?.reduce((sum: number, expense: any) => sum + (expense.value || 0), 0) || 0;
      const totalExcludingRE =
        expenses?.reduce((sum: number, expense: any) => {
          if (expense.category === 'Real Estate Taxes') return sum;
          return sum + (expense.value || 0);
        }, 0) || 0;

      totalValues[fieldName] = total;
      totalExcludingREValues[fieldName] = totalExcludingRE;
    });
    setTotalValues(totalValues);
    setTotalExcludingREValues(totalExcludingREValues);
  };

  const rows = useMemo(() => {
    const yearCount = expenseHistory.length || 1;
    const updatedRows: ExpenseRow[] = expenseCategories.map((row: any) => {
      const { key } = row;
      const updatedRow: ExpenseRow = { id: key, ...row };
      const yearValues: number[] = [];

      expenseHistory.forEach((expenseHistoryItem: any) => {
        const expenses = expenseHistoryItem.expenses || [];
        const fieldName = `${expenseHistoryItem.period}-${expenseHistoryItem.year}`;
        const category = row.category;
        const expenseValue = find(expenses, (expense) => expense.category === category)?.value || 0;
        updatedRow[fieldName] = expenseValue;
        yearValues.push(expenseValue);
      });

      updatedRow.average =
        divide(
          yearValues.reduce((sum, val) => sum + val, 0),
          yearCount
        ) || 0;
      return updatedRow;
    });

    const totalOperatingExpenses: ExpenseRow = {
      id: 'total-operating-expenses',
      category: 'Total Operating Expenses',
      isTotal: true,
      average: 0,
    };

    const totalExcludingRETaxes: ExpenseRow = {
      id: 'total-excluding-re-taxes',
      category: 'Total Operating Expenses (Excl. RE Taxes)',
      isTotal: true,
      average: 0,
    };

    Object.keys(totalValues).forEach((key: string) => {
      totalOperatingExpenses[key] = get(totalValues, key, 0);
      totalExcludingRETaxes[key] = get(totalExcludingREValues, key, 0);
    });

    totalOperatingExpenses.average =
      divide(
        Object.values<number>(totalValues).reduce((sum, val) => sum + val, 0),
        yearCount
      ) || 0;

    totalExcludingRETaxes.average =
      divide(
        Object.values<number>(totalExcludingREValues).reduce((sum, val) => sum + val, 0),
        yearCount
      ) || 0;

    return [...updatedRows, totalOperatingExpenses, totalExcludingRETaxes];
  }, [expenseHistory, expenseCategories, totalValues, totalExcludingREValues]);

  const columns = useMemo(() => {
    const baseColumns = getBaseColumns(expenseHistory, deleteColumn);
    const actionColumn: GridColDef = {
      field: 'action',
      headerName: 'Action',
      type: 'actions',
      width: 100,
      getActions: (params: GridRowParams) => {
        if (params.row.isTotal) return [];

        return [
          <IconButton key="menu" size="small" onClick={(event) => handleMenuClick(event, params.id)}>
            <MoreHorizIcon fontSize="small" />
          </IconButton>,
        ];
      },
    };

    return [...baseColumns, actionColumn];
  }, [expenseHistory, deleteColumn]);

  return (
    <Box sx={{ pt: 2 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 500 }}>
          Expense History
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" size="small" onClick={handleClickOpen}>
            ADD CATEGORY
          </Button>
        </Box>
      </Box>

      <Table
        rows={rows}
        columns={columns}
        processRowUpdate={handleProcessRowUpdate}
        onProcessRowUpdateError={(error) => console.error('Row update error:', error)}
        getRowClassName={(params) => (params.row.isTotal ? 'total-row' : '')}
        getCellClassName={(params) => {
          const { row, field } = params;
          if ((field === 'category' || field === 'average') && !row.isTotal) {
            return 'disabled-cell';
          }
          return '';
        }}
        sx={{ ...EXPENSE_HISTORY_TABLE_BASE_SX }}
        isCellEditable={(params) => {
          if (params.row.isTotal) {
            return false;
          }

          return true;
        }}
        columnHeaderHeight={undefined}
      />

      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        <MenuItem onClick={handleDeleteCategory} sx={{ color: 'error.main' }}>
          <DeleteIcon sx={{ mr: 1, fontSize: 16 }} />
          Delete
        </MenuItem>
      </Menu>

      {isModalOpen && (
        <AddExpenseDialog
          field={expenseNameField}
          excludedExpenses={expenseCategories.map((expenseCategory: any) => expenseCategory.category)}
          name="Add Custom Expense Category"
          onCancel={handleClickClose}
          onSave={addCategory}
        />
      )}
    </Box>
  );
});

export default ExpenseHistoryTable;
